import { motion } from 'framer-motion';
 
const fadeUp = {
    hidden: { opacity: 0, y: 16 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, ease: 'easeOut' },
    },
};
 
interface PolicySection {
    heading?: string;
    paragraphs?: string[];
    bullets?: string[];
    subSections?: { label: string; bullets: string[] }[];
}
 
const sections: PolicySection[] = [
    {
        paragraphs: [
            'JAPFA COMFEED INDIA PVT. LTD. ("JAPFA", "Company", "we", "our", or "us") is committed to protecting the privacy and security of information collected through the Japfa Feed App, including its Android mobile application and Angular web dashboard (collectively referred to as the "Platform").',
            'This Privacy Policy describes how we collect, use, store, process, and protect information when Sales Executives, Managers, and Administrators access or use the Platform for feed sales, customer management, field visits, and order processing activities.',
            'By using the Platform, you agree to the collection and use of information in accordance with this Privacy Policy.',
        ],
    },
    {
        heading: '2. Purpose of the Application',
        paragraphs: [
            'The Japfa Feed App is an internal Sales Force Automation and Sales Operations platform designed to support the complete feed-sales lifecycle, including customer management, field visit and route tracking, purchase order creation, product and stock selection, integration with the Company\'s SAP system for sales-order processing, delivery tracking, target versus actual performance monitoring, dashboards, and customer feedback collection.',
            'The application is intended solely for authorized business and operational purposes related to feed sales, field force management, and supply chain coordination, and interfaces with the Company\'s backend SAP and MySQL database systems to keep field-level records synchronized with enterprise records.',
        ],
    },
    {
        heading: '3. Information We Collect',
        paragraphs: [
            'Depending on your role and usage of the Platform, we may collect the following information:',
        ],
        subSections: [
            {
                label: 'A. Personal Information',
                bullets: [
                    'Full Name',
                    'Login Credentials and Device ID (for mobile authentication)',
                    'Mobile Number and Email Address',
                    'Role and Access Level (Sales Executive, Manager, Administrator)',
                    'Division, Region, and Reporting Structure Details',
                ],
            },
            {
                label: 'B. Customer Information',
                bullets: [
                    'Customer Name, Contact Details, and Address',
                    'Customer Assignment to Sales Executive and Division',
                    'Customer Enquiry and Lead Details',
                    'Customer Order Status and Sales History',
                ],
            },
            {
                label: 'C. Field Visit and GPS Information',
                bullets: [
                    'Visit Date, Time, and Route Details',
                    'Real-Time GPS Waypoints During Field Visits',
                    'Daily Visit Targets and Customer Visit Counts',
                    'Travel Distance and Visit History',
                ],
            },
            {
                label: 'D. Order and Transaction Information',
                bullets: [
                    'Purchase Order Header and Line-Item Details (products, quantities, plant/division)',
                    'Purchase Order Status (New, Pending, Approved, Dispatched, Delivered, Cancelled)',
                    'Purchase Order Audit History (before/after change records)',
                    'SAP Sales Order Reference Numbers',
                    'Delivery Challan and Transport Records',
                    'Available Stock, Product, and Plant Data',
                ],
            },
            {
                label: 'E. Performance and Feedback Information',
                bullets: [
                    'Target versus Actual Sales Data (individual, division, and product-level)',
                    'Sales Manager Target Records',
                    'Customer Feedback Form Responses',
                    'Saved Report Configurations and Dashboard Preferences',
                ],
            },
            {
                label: 'F. Device and Technical Information',
                bullets: [
                    'Device Model, Operating System, and Device ID',
                    'Application Version (checked against active version on server)',
                    'IP Address, Session, and Login Activity',
                    'System and Audit Logs',
                    'Uploaded Files and Attachments',
                ],
            },
        ],
    },
    {
        heading: '4. Location Information',
        paragraphs: [
            'The Platform collects GPS location information from mobile devices during active field visits by Sales Executives.',
            'Location information may be used for:',
        ],
        bullets: [
            'Recording customer visit routes and GPS waypoints',
            'Verifying field visits and calculating travel distance',
            'Tracking daily visit targets against actual customer visits',
            'Operational planning, monitoring, and territory management',
        ],
    },
    {
        heading: '5. Camera and Attachment Access',
        paragraphs: [
            'The Platform may request access to the device camera and file storage to:',
        ],
        bullets: [
            'Upload supporting documents and attachments related to orders and visits',
            'Capture and upload images associated with customer records or feedback forms',
            'Store application-related files required for offline or field data entry',
        ],
    },
    {
        heading: '6. How We Use Information',
        paragraphs: [
            'Information collected through the Platform may be used to:',
        ],
        bullets: [
            'Authenticate users and manage secure access to the Platform',
            'Assign and manage customers, territories, and sales targets',
            'Record and monitor field visits and GPS-based route tracking',
            'Create, process, and track purchase orders through to SAP Sales Order creation',
            'Reconcile purchase orders with SAP Sales Orders and monitor delivery status',
            'Generate target versus actual performance reports and dashboards',
            'Collect and analyze customer feedback',
            'Maintain system security, audit trails, and provide technical support',
            'Comply with applicable legal and regulatory requirements',
        ],
    },
    {
        heading: '7. Data Sharing and Disclosure',
        paragraphs: [
            'We do not sell personal information.',
            'Information may be shared only under the following circumstances:',
        ],
        subSections: [
            {
                label: 'Authorized Internal Users',
                bullets: [
                    'Information may be accessed by authorized Sales Executives, Managers, and Administrators who require access for legitimate sales, field operations, and supervisory purposes.',
                ],
            },
            {
                label: 'Enterprise Systems',
                bullets: [
                    'Data is exchanged with the Company\'s SAP ERP system through SAP JCo/RFC integration and scheduled file-based synchronization for orders, stock, sales targets, and delivery records.',
                ],
            },
            {
                label: 'Service Providers',
                bullets: [
                    'Cloud and server hosting',
                    'Technical support and application maintenance',
                    'Database and file storage',
                    'System monitoring (e.g., Prometheus and Grafana)',
                ],
            },
            {
                label: 'Legal Requirements',
                bullets: [
                    'Applicable laws',
                    'Government and regulatory authorities',
                    'Court orders',
                    'Law enforcement agencies',
                ],
            },
        ],
    },
    {
        heading: '8. Data Retention',
        paragraphs: ['We retain information only for as long as necessary to:'],
        bullets: [
            'Support ongoing sales operations and customer relationship management',
            'Maintain purchase order, delivery, and audit history records',
            'Fulfill legal and regulatory obligations',
            'Reconcile orders and sales data with SAP',
            'Resolve disputes or claims',
            'Enforce company policies',
        ],
    },
    {
        heading: '9. Data Security',
        paragraphs: [
            'We implement reasonable administrative, technical, and organizational safeguards to protect information against unauthorized access, disclosure, alteration, or destruction.',
            'Security measures may include:',
        ],
        bullets: [
            'JWT-based stateless authentication with BCrypt-hashed credentials',
            'Device ID binding for mobile authentication',
            'Role-based access control (Sales Executive, Manager, Administrator)',
            'Division-based data filtering and access restrictions',
            'Detailed purchase order audit history (before/after change tracking)',
            'Administrative audit logs and operational monitoring',
            'Periodic security reviews',
        ],
    },
    {
        heading: '10. User Responsibilities',
        paragraphs: ['Users are responsible for:'],
        bullets: [
            'Maintaining confidentiality of login credentials',
            'Protecting authorized devices used to access the Platform',
            'Submitting accurate and timely customer, visit, and order information',
            'Reporting unauthorized access or suspected security incidents',
            'Using the Platform only for authorized sales and business activities',
        ],
    },
    {
        heading: "11. Children's Privacy",
        paragraphs: [
            'The Platform is intended solely for authorized business users and is not directed toward individuals under the age of 18 years.',
            'We do not knowingly collect personal information from children.',
        ],
    },
    {
        heading: '12. Your Rights',
        paragraphs: ['Subject to applicable laws, users may request:'],
        bullets: [
            'Access to their personal information',
            'Correction of inaccurate information',
            'Deletion of information where legally permissible',
            'Restriction of processing under certain circumstances',
        ],
    },
    {
        heading: '13. Account Deletion Request',
        paragraphs: [
            'Users may request deletion of their account and associated personal information by contacting the Feed App Support Team.',
            'Certain order, transaction, financial, or compliance records may be retained where required by applicable laws or legitimate business requirements.',
            'For account deletion requests, contact: buddyassist.jcipl@japfa.com',
        ],
    },
    {
        heading: '14. Changes to this Privacy Policy',
        paragraphs: [
            'We may update this Privacy Policy from time to time to reflect operational, legal, regulatory, or technological changes.',
            "The latest version will always be available through the Platform and on the Company's website.",
            'Continued use of the Platform after any updates constitutes acceptance of the revised Privacy Policy.',
        ],
    },
    {
        heading: '15. Contact Us',
        paragraphs: [
            'If you have questions regarding this Privacy Policy or the handling of your information, please contact:',
            'JAPFA COMFEED INDIA PVT. LTD.\nPlot No. 91, Sakore Nagar\nViman Nagar\nPune – 411014\nIndia',
            'Email: buddyassist.jcipl@japfa.com',
            'Website: https://www.japfaindia.com',
        ],
    },
];
 
const Feedpolicy = () => {
    return (
        <>
            <section className="flex justify-center bg-[#160700] py-[60px] ps-8 align-middle lg:ps-14 xl:py-[80px]">
                <motion.div
                    className="my-auto flex flex-col items-start justify-center sm:w-[60%] md:w-[60%]"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={fadeUp}
                >
                    <h1 className="text-[28px] font-bold leading-[120%] text-white lg:text-[40px] xl:text-[52px]">
                        Privacy Policy – Japfa Feed App
                    </h1>
                </motion.div>
            </section>
 
            {/* Policy Body */}
            <section className="bg-white px-6 py-12 sm:px-10 md:px-16 lg:px-24 xl:px-32">
                <div className="">
                    {/* Introduction paragraph before numbered sections */}
                    <motion.div
                        className="mb-6"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeUp}
                    >
                        <h2 className="mb-2 text-[16px] font-bold text-[#1b1b1b]">
                            1. Introduction
                        </h2>
                        {sections[0].paragraphs?.map((p, i) => (
                            <p
                                key={i}
                                className="mb-3 text-justify text-[15px] leading-[1.75] text-[#1b1b1b]"
                            >
                                {p}
                            </p>
                        ))}
                    </motion.div>
 
                    {/* Remaining sections */}
                    {sections.slice(1).map((section, sIdx) => (
                        <motion.div
                            key={sIdx}
                            className="mb-6"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.1 }}
                            variants={fadeUp}
                        >
                            {section.heading && (
                                <h2 className="mb-2 text-[16px] font-bold text-[#1b1b1b]">
                                    {section.heading}
                                </h2>
                            )}
 
                            {section.paragraphs?.map((p, i) => (
                                <p
                                    key={i}
                                    className="mb-3 whitespace-pre-line text-justify text-[15px] leading-[1.75] text-[#1b1b1b]"
                                >
                                    {p}
                                </p>
                            ))}
 
                            {section.bullets && section.bullets.length > 0 && (
                                <ul className="mb-3 ml-5 list-disc space-y-1">
                                    {section.bullets.map((b, i) => (
                                        <li
                                            key={i}
                                            className="text-[15px] leading-[1.75] text-[#1b1b1b]"
                                        >
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                            )}
 
                            {section.subSections?.map((sub, subIdx) => (
                                <div key={subIdx} className="mb-4 ml-2">
                                    <p className="mb-1 text-[15px] font-semibold text-[#1b1b1b]">
                                        {sub.label}
                                    </p>
                                    <ul className="ml-5 list-disc space-y-1">
                                        {sub.bullets.map((b, i) => (
                                            <li
                                                key={i}
                                                className="text-[15px] leading-[1.75] text-[#1b1b1b]"
                                            >
                                                {b}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </motion.div>
                    ))}
                </div>
            </section>
        </>
    );
};
 
export default Feedpolicy;
